import { db } from "config/firebase";
import { Timestamp, collection, query, where, getDocs, writeBatch, doc, updateDoc, getDoc, deleteDoc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import { createPromotionDoc, computeIsActive, queryPromotionsByBatchId } from './promotionCollectionHelpers';

/**
 * Parse discount amount from various formats
 * Strips $ symbols, validates numeric, blocks % symbols
 * @param {string|number} value - The raw value from CSV
 * @returns {Object} { valid: boolean, value: number, error: string }
 */
export function parseAmount(value) {
  if (!value && value !== 0) {
    return { valid: false, value: null, error: 'Amount is required' };
  }

  const strValue = String(value).trim();
  
  // Block percentage symbols
  if (strValue.includes('%')) {
    return { valid: false, value: null, error: 'Percentage discounts not supported (use fixed dollar amount)' };
  }

  // Strip $ and comma symbols
  const cleaned = strValue.replace(/[$,]/g, '');
  const parsed = Number(cleaned);

  if (isNaN(parsed)) {
    return { valid: false, value: null, error: 'Amount must be numeric' };
  }

  if (parsed <= 0) {
    return { valid: false, value: null, error: 'Amount must be greater than 0' };
  }

  return { valid: true, value: parsed, error: null };
}

/**
 * Convert Excel serial date to JavaScript Date
 * Excel dates are days since January 1, 1900 (with a leap year bug for 1900)
 */
function excelDateToJSDate(excelDate) {
  // Excel's epoch is December 30, 1899 (accounting for the 1900 leap year bug)
  const excelEpoch = new Date(1899, 11, 30);
  const msPerDay = 24 * 60 * 60 * 1000;
  return new Date(excelEpoch.getTime() + excelDate * msPerDay);
}

/**
 * Parse date from multiple formats
 * Handles YYYY-MM-DD, MM/DD/YYYY, M/D/YY, and Excel serial dates
 * @param {string|Date|number} value - The raw date value
 * @returns {Object} { valid: boolean, value: Date, error: string }
 */
export function parseDate(value) {
  if (!value && value !== 0) {
    return { valid: false, value: null, error: 'Date is required' };
  }

  // If already a Date object
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? { valid: false, value: null, error: 'Invalid date' }
      : { valid: true, value: value, error: null };
  }

  const strValue = String(value).trim();

  // Try parsing various formats
  let date;

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
    date = new Date(strValue + 'T00:00:00');
  }
  // MM/DD/YYYY or M/D/YYYY
  else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(strValue)) {
    const [month, day, year] = strValue.split('/');
    date = new Date(year, month - 1, day);
  }
  // M/D/YY (2-digit year)
  else if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(strValue)) {
    const [month, day, year] = strValue.split('/');
    const fullYear = parseInt(year) + 2000; // Assume 2000s
    date = new Date(fullYear, month - 1, day);
  }
  // Excel serial date (number between ~1 and ~50000 represents dates 1900-2036)
  else if (!isNaN(value) && typeof value === 'number' && value > 1 && value < 100000) {
    date = excelDateToJSDate(value);
  }
  // JavaScript timestamp (large number)
  else if (!isNaN(value) && typeof value === 'number' && value > 100000) {
    date = new Date(value);
  }
  else {
    return { valid: false, value: null, error: 'Invalid date format (use YYYY-MM-DD, MM/DD/YYYY, or M/D/YY)' };
  }

  if (isNaN(date.getTime())) {
    return { valid: false, value: null, error: 'Invalid date' };
  }

  return { valid: true, value: date, error: null };
}

/**
 * Validate date logic (start < end, not expired)
 * @param {Date} startDate 
 * @param {Date} endDate 
 * @param {boolean} skipExpired - Whether to flag expired dates as errors
 * @returns {Object} { valid: boolean, warning: string, error: string }
 */
export function validateDateLogic(startDate, endDate, skipExpired = true) {
  const now = new Date();
  
  if (startDate >= endDate) {
    return { valid: false, warning: null, error: 'Start date must be before end date' };
  }

  if (endDate < now) {
    if (skipExpired) {
      return { valid: false, warning: null, error: 'End date is in the past' };
    } else {
      return { valid: true, warning: 'End date is in the past', error: null };
    }
  }

  return { valid: true, warning: null, error: null };
}

/**
 * Check if SKU is a variant (contains hyphen)
 * @param {string} sku 
 * @returns {Object} { isVariant: boolean, error: string }
 */
export function validateSKU(sku) {
  if (!sku || String(sku).trim() === '') {
    return { isVariant: false, error: 'SKU is required' };
  }

  const strSKU = String(sku).trim();
  
  // Variant SKUs have hyphens (e.g., 451740-30)
  if (strSKU.includes('-')) {
    const parentSKU = strSKU.split('-')[0];
    return { isVariant: true, error: `Use parent SKU ${parentSKU} (not variant SKU)` };
  }

  return { isVariant: false, error: null };
}

/**
 * Check if discount already exists (semantic duplicate detection)
 * @param {Object} newDiscount - The discount to check
 * @param {Array} existingDiscounts - Array of existing discounts
 * @returns {boolean} True if duplicate exists
 */
export function checkDuplicate(newDiscount, existingDiscounts) {
  if (!existingDiscounts || existingDiscounts.length === 0) return false;

  return existingDiscounts.some(d => {
    // Compare name, value, and dates
    const nameMatch = d.name === newDiscount.name;
    const valueMatch = Math.abs(d.value - newDiscount.value) < 0.01; // Float comparison
    
    // Handle both Date and Timestamp objects
    const startMatch = (d.startDate instanceof Timestamp 
      ? d.startDate.toDate() 
      : d.startDate
    ).getTime() === newDiscount.startDate.getTime();
    
    const endMatch = (d.endDate instanceof Timestamp 
      ? d.endDate.toDate() 
      : d.endDate
    ).getTime() === newDiscount.endDate.getTime();

    return nameMatch && valueMatch && startMatch && endMatch;
  });
}

/**
 * Generate CSV template for download
 * @param {boolean} withSamples - Include sample data rows
 * @returns {Blob} CSV blob for download
 */
export function generateTemplate(withSamples = false) {
  const headers = ['SKU', 'Promotion Name', 'Discount Amount', 'Start Date', 'End Date', 'Notes'];
  
  const rows = [headers];
  
  if (withSamples) {
    rows.push(['451740', 'Spring Sale', '1.00', '2026-03-01', '2026-03-31', 'Q1 clearance']);
    rows.push(['400032', 'Summer Deal', '2.50', '2026-06-01', '2026-06-30', 'Seasonal promo']);
    rows.push(['451740', 'Holiday Sale', '1.50', '2026-12-01', '2026-12-31', 'Year-end promo']);
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Promotions');
  
  const wbout = XLSX.write(wb, { bookType: 'csv', type: 'binary' });
  
  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
  };

  return new Blob([s2ab(wbout)], { type: 'text/csv' });
}

/**
 * Batch write promotions to Firebase
 * Includes chunking for >500 writes and audit trail
 * @param {Array} validRows - Array of validated promotion objects
 * @param {Object} user - Firebase user object
 * @param {string} batchId - UUID for this import batch
 * @param {Function} progressCallback - Optional callback for progress updates
 * @returns {Object} { success: number, failed: Array }
 */
export async function batchWritePromotions(validRows, user, batchId, progressCallback = null) {
  const results = { success: 0, failed: [] };
  
  // Process each row sequentially for dual-write
  for (const row of validRows) {
    try {
      const { sku, name, value, startDate, endDate, notes, productDocRef } = row;

      // Create discount object with audit fields
      const newDiscount = {
        name,
        value,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        notes: notes || '',
        createdBy: user.email,
        createdAt: Timestamp.now(),
        createdVia: 'bulk_upload',
        batchId
      };

      // DUAL-WRITE STRATEGY:
      // 1. Update product.discounts array (for SellerActive sync)
      const existingDiscounts = row.existingDiscounts || [];
      const updatedDiscounts = [...existingDiscounts, newDiscount];
      await updateDoc(productDocRef, { discounts: updatedDiscounts });

      // 2. Create document in promotions collection (for fast grid queries)
      const productDoc = await getDoc(productDocRef);
      const productName = productDoc.data()?.name || sku;
      
      await createPromotionDoc(sku, newDiscount, productDocRef, productName);

      results.success++;
      
      if (progressCallback) {
        progressCallback(results.success, validRows.length);
      }
    } catch (error) {
      console.error('Batch write error:', error);
      results.failed.push({ sku: row.sku, error: error.message });
    }
  }

  return results;
}

/**
 * Calculate units sold during promotion period
 * Queries orders collection for SKU + date range
 * @param {string} sku 
 * @param {Date} startDate 
 * @param {Date} endDate 
 * @returns {number} Total units sold
 */
export async function calculateUnitsSold(sku, startDate, endDate) {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('items', 'array-contains', { sku }), // Simplified - actual structure may vary
      where('createDate', '>=', Timestamp.fromDate(startDate)),
      where('createDate', '<=', Timestamp.fromDate(endDate))
    );

    const querySnapshot = await getDocs(q);
    let totalUnits = 0;

    querySnapshot.forEach((doc) => {
      const order = doc.data();
      // Find matching SKU in items and sum quantities
      order.items?.forEach((item) => {
        if (item.sku === sku) {
          totalUnits += item.quantity || 0;
        }
      });
    });

    return totalUnits;
  } catch (error) {
    console.error('Error calculating units sold:', error);
    return 0;
  }
}

/**
 * Rollback an entire import by batchId
 * Removes all discounts matching the batchId
 * @param {string} batchId - The batch ID to rollback
 * @returns {Object} { success: boolean, removedCount: number, error: string }
 */
export async function rollbackImport(batchId) {
  try {
    // OPTIMIZED: Query promotions collection by batchId (indexed, fast)
    const promotionsToRemove = await queryPromotionsByBatchId(batchId);
    
    if (promotionsToRemove.length === 0) {
      return { success: true, removedCount: 0, error: 'No promotions found for this batch' };
    }

    // Group promotions by product for efficient batch updates
    const promosByProduct = {};
    promotionsToRemove.forEach(promo => {
      const productPath = promo.productRef.path;
      if (!promosByProduct[productPath]) {
        promosByProduct[productPath] = {
          ref: promo.productRef,
          promoIds: [],
          promos: []
        };
      }
      promosByProduct[productPath].promoIds.push(promo.id);
      promosByProduct[productPath].promos.push(promo);
    });

    let removedCount = 0;

    // Process each product: remove from both locations
    for (const productPath in promosByProduct) {
      const { ref: productRef, promoIds, promos } = promosByProduct[productPath];
      
      try {
        // 1. Remove from product.discounts array
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          const currentDiscounts = productDoc.data().discounts || [];
          const filteredDiscounts = currentDiscounts.filter(d => d.batchId !== batchId);
          await updateDoc(productRef, { discounts: filteredDiscounts });
        }

        // 2. Delete from promotions collection
        for (const promoId of promoIds) {
          await deleteDoc(doc(db, 'promotions', promoId));
          removedCount++;
        }
      } catch (error) {
        console.error(`Error rolling back product ${productPath}:`, error);
      }
    }

    // Update import history to mark as rolled back
    const historyRef = collection(db, 'promotionImportHistory');
    const historyQuery = query(historyRef, where('batchId', '==', batchId));
    const historySnapshot = await getDocs(historyQuery);

    if (!historySnapshot.empty) {
      const historyDoc = historySnapshot.docs[0];
      await updateDoc(historyDoc.ref, { status: 'rolled_back', rolledBackAt: Timestamp.now() });
    }

    return { success: true, removedCount, error: null };
  } catch (error) {
    console.error('Rollback error:', error);
    return { success: false, removedCount: 0, error: error.message };
  }
}

/**
 * Check if product exists in Firebase by SKU
 * @param {string} sku 
 * @returns {Object} { exists: boolean, docRef: DocumentReference, discounts: Array }
 */
export async function checkProductExists(sku) {
  try {
    const productsRef = collection(db, 'productCatalog', 'topicz', 'products');
    const q = query(productsRef, where('sku', '==', sku));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { exists: false, docRef: null, discounts: [] };
    }

    const productDoc = querySnapshot.docs[0];
    const product = productDoc.data();

    return { 
      exists: true, 
      docRef: productDoc.ref, 
      discounts: product.discounts || [] 
    };
  } catch (error) {
    console.error('Error checking product:', error);
    return { exists: false, docRef: null, discounts: [], error: error.message };
  }
}
